/**
 * +autoattack: auto-attack clips beyond the class swing (anim/defaults.ts makeSwing, which plays
 * for main-hand swings and casters' staff bonks):
 *  - the off-hand swing of a dual wielder (the Rogue): the main-hand slash mirrored, the left
 *    blade leading, so main and off hand alternate visibly;
 *  - Auto Shot: raise the bow (left hand), draw the string to the cheek, release (the arrow itself
 *    is the frame's projectile, game/frame.ts).
 * Pose channels are mirrored left / right (anim/clips.ts), so the same numbers mean the same motion.
 */
import { AUTO_SHOT_ABILITY_ID } from '@keystone/sim';
import { defineClip } from '../clips';
import { CLASS_LOOK } from '../defaults';
import { registerAbilityClips } from '../registry';

/** Pseudo ability id of an off-hand white swing cue (game/frame.ts eventsToCues). */
export const OFFHAND_CLIP_ID = 'melee.oh';

const look = CLASS_LOOK['blademaster']!;

export const offhandSwing = defineClip({
  id: OFFHAND_CLIP_ID,
  duration: 0.48,
  keys: [
    {
      t: 0.13,
      ease: 'out',
      pose: {
        spine: [-0.05, 0.36, 0],
        chest: [0, 0.14, 0],
        head: [0, -0.22, 0],
        leftUpperArm: [2.1, -0.15, 0.8],
        leftLowerArm: [0.55],
        rightUpperArm: [0.5, 0.1, 0.45],
        rightLowerArm: [0.7],
        rightUpperLeg: [0.12],
        rightLowerLeg: [0.25],
      },
      root: { lunge: -0.05 },
    },
    {
      t: 0.21,
      ease: 'snap',
      hold: 0.04,
      pose: {
        spine: [0.2, -0.4, 0],
        chest: [0.05, -0.18, 0],
        head: [-0.08, 0.3, 0],
        leftUpperArm: [0.7, 0.95, 0.2],
        leftLowerArm: [0.08],
        rightUpperArm: [0.45, 0.2, 0.4],
        rightLowerArm: [0.6],
        rightUpperLeg: [0.4],
        rightLowerLeg: [0.3],
        leftUpperLeg: [-0.15],
      },
      root: { lunge: 0.2 },
    },
    { t: 0.48, ease: 'out' },
  ],
  trails: [{ from: 0.11, to: 0.28, hand: 'left', color: look.color, tail: look.tail, style: look.swingStyle, width: 0.75, fade: 0.16 }],
  cues: [{ t: 0.21, fx: 'sparks', at: 'tip', scale: 0.5 }],
});

const DRAW = {
  spine: [0.02, 0.35, 0],
  chest: [0, 0.15, 0],
  head: [0, -0.4, 0],
  leftUpperArm: [1.45, 0.25, 0.15],
  leftLowerArm: [0.05],
  rightUpperArm: [1.35, 0.75, 0.35],
  rightLowerArm: [2.0],
} as const;

export const autoShot = defineClip({
  id: 'auto_shot.draw',
  duration: 0.62,
  blendIn: 0.06,
  mask: 'upper',
  keys: [
    { t: 0.16, ease: 'out', pose: { ...DRAW, rightUpperArm: [1.4, 0.95, 0.3], rightLowerArm: [1.2] } }, // nock: hands together
    { t: 0.3, ease: 'inOut', hold: 0.04, pose: DRAW }, // full draw to the cheek
    { t: 0.36, ease: 'snap', pose: { ...DRAW, rightUpperArm: [1.15, 0.35, 0.75], rightLowerArm: [1.5] } }, // release: the hand flies back
    { t: 0.62, ease: 'out' },
  ],
});

export function registerAutoAttackClips(): void {
  registerAbilityClips(OFFHAND_CLIP_ID, { strike: offhandSwing });
  registerAbilityClips(AUTO_SHOT_ABILITY_ID, { strike: autoShot });
}
